import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";

import { MunicipalitySetupChecklist } from "@/components/MunicipalitySetupChecklist";
import { Button } from "@/components/ui/Button";
import { CheckboxInput } from "@/components/ui/Checkbox";
import { Dialog } from "@/components/ui/Dialog";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { ApiError, apiFetch } from "@/lib/api";
import type { EquipmentResource, MunicipalityResource, Page, PropertyResource, ReferenceListItemResource, UnitResource } from "@/lib/api-types";

import { baselineTemplateForPropertyType } from "./baselineEquipmentTemplates";

/**
 * The real, two-step Add Property wizard (Story 5.5, R-V5) — replaces
 * Story 5.3's own `CreatePropertyDialog`, which existed only to give that
 * story's `POST /properties` write path something real to call, never the
 * product's real creation flow (spec Intent).
 *
 * Step 1 (`"property"`): every §4.1 field this story's own AC names —
 * `full_address`, `municipality_id`, `property_type`, `year_built`,
 * `built_before_1978` — plus the municipality picker's own "create a new
 * town" sub-flow (`POST /municipalities`, Story 5.5's own new route) and,
 * only right after a successful creation, the shared `MunicipalitySetup
 * Checklist` rendered inline (judgement calls 3/4: never required before
 * the property write, never shown for an existing town regardless of its
 * own `setup_complete`).
 *
 * `built_before_1978` (Owner-requested, 2026-08-28): no longer its own
 * question — `deriveBuiltBefore1978` computes it from `year_built`, asked
 * immediately above it in this same step, rather than asking the Owner to
 * answer the same real-world fact twice. Still a real field on the write
 * (`POST /properties`'s own body still carries it), still shown and
 * independently editable afterward on the property record page — this
 * removes only the redundant second question here, not the field itself.
 *
 * Step 2 (`"units"`): `property_type === "sfh"` is a confirmation of the one
 * real, `intent="rentable"` unit `POST /properties` itself already creates
 * (judgement call 2) — never a second, parallel decision about unit count.
 * Every other property type is free-form: one or more unit-label rows.
 *
 * Step 3 (`"equipment"`, Story 12.2, R-P3) — a real, computed baseline
 * equipment proposal for the chosen `property_type`
 * (`baselineEquipmentTemplates.ts`, hand-mirrored from `app/domain/
 * equipment/baseline_templates.py` — VC-12.2-12/13's own "no new route"
 * decision), rendered as a pre-checked checklist (spec judgement call 3):
 * every proposed row can be unchecked (removed) before Finish, and the
 * Owner can add any other real seeded `equipment_category` beyond the
 * template (spec judgement call 5: a hand-added row is always building-
 * level — simpler and defensible, the Owner can always move to Story
 * 12.1's own capture flow immediately after Finish for anything more
 * specific). `mixed_use` (and any unrecognized `property_type`) renders an
 * honest "no baseline proposed" message instead of an empty, unexplained
 * list.
 *
 * Nothing is written until "Finish" (judgement call 6/AC "nothing is
 * written until the final step commits") — every field lives in this
 * component's own local state, survives Back/Forward navigation across all
 * three steps, and is discarded (never persisted anywhere) if the dialog is
 * closed before that. "Finish" creates the property first; on success, and
 * only for a non-single-family property, creates each entered unit in
 * sequence (judgement call 1) — a failure partway is reported honestly,
 * naming exactly which units landed and which did not, never a silent
 * partial success or a bare generic failure message. Once every property/
 * unit creation call has resolved, one `POST /equipment` is made per
 * confirmed baseline row (spec judgement call 4: only units that actually,
 * successfully exist — read from `unitOutcomes`'s own successful entries,
 * never assumed from the Owner's typed-in unit count) — a natural-key
 * collision, or any other failure, on any one row is a silent no-op for
 * that row alone (mirrors `create_equipment`'s own documented sweep
 * contract), never surfaced to the Owner and never aborting the rest of
 * this sequence (VC-12.2-07).
 *
 * The confirmation view shows a real elapsed-time figure (judgement call 5:
 * no analytics pipeline exists anywhere in this codebase, so this is a
 * real, visible on-screen timer — the clock starts the moment this wizard
 * opens and stops the moment the property is created).
 */

const SINGLE_FAMILY_PROPERTY_TYPE = "sfh";
//: `app/repo/units.py::create_property_with_units`'s own `rentable_unit_
//: label` default — the label `POST /properties` itself gives a single-
//: family property's one real unit. Mirrored here as a literal, not a
//: shared constant (the identical discipline VC-5.4-15 already established
//: for `SINGLE_FAMILY_PROPERTY_TYPE` itself: this value is referenced by
//: its own string on both sides, never imported across the frontend/backend
//: boundary).
const SINGLE_FAMILY_UNIT_LABEL = "Unit 1";

type WizardStep = "property" | "units" | "equipment";

type UnitOutcome =
  | { label: string; id: string; status: "created" }
  | { label: string; status: "failed"; message: string };

interface FinishedResult {
  property: PropertyResource;
  unitOutcomes: UnitOutcome[];
  elapsedMs: number;
}

//: Step 3's own proposal row shape (Story 12.2) — `target.kind ===
//: "building"` resolves to `unit_id: null` at seed time (the server itself
//: resolves the real sentinel unit, AD-18, exactly like `AddEquipmentDialog.
//: tsx`'s own building-level choice); `target.kind === "unit"` carries a
//: plain `unitIndex` into whichever unit-label list step 2 actually intends
//: (`intendedUnitLabels` below) — resolved to a *real* unit id only at
//: Finish time, from `sfhUnitId`/`unitOutcomes`, never stored here.
type EquipmentTarget = { kind: "building" } | { kind: "unit"; unitIndex: number };

interface EquipmentProposalRow {
  key: string;
  category: string;
  target: EquipmentTarget;
}

//: Owner-requested (2026-08-28): the same slot (unit or building-level) can
//: now hold more than one item of the same category, told apart by
//: `specific_location` (`app/repo/models/equipment.py`'s own widened
//: natural key, migration `0049`) — `duplicateRowKeys` names every row that
//: shares its `(category, target)` with at least one other row currently
//: proposed, so the location field only ever appears where it is actually
//: needed to disambiguate, never on an ordinary single-of-a-kind row.
function targetKey(target: EquipmentTarget): string {
  return target.kind === "building" ? "building" : `unit:${target.unitIndex}`;
}

function duplicateRowKeys(rows: EquipmentProposalRow[]): Set<string> {
  const countByGroup = new Map<string, number>();
  for (const row of rows) {
    const group = `${row.category}::${targetKey(row.target)}`;
    countByGroup.set(group, (countByGroup.get(group) ?? 0) + 1);
  }
  const duplicates = new Set<string>();
  for (const row of rows) {
    const group = `${row.category}::${targetKey(row.target)}`;
    if ((countByGroup.get(group) ?? 0) > 1) {
      duplicates.add(row.key);
    }
  }
  return duplicates;
}

function templateRowsFor(propertyType: string, intendedUnitLabels: string[]): EquipmentProposalRow[] {
  const rows: EquipmentProposalRow[] = [];
  for (const templateRow of baselineTemplateForPropertyType(propertyType)) {
    if (templateRow.isBuildingLevel) {
      rows.push({
        key: `template:building:${templateRow.category}`,
        category: templateRow.category,
        target: { kind: "building" },
      });
    } else {
      intendedUnitLabels.forEach((_label, unitIndex) => {
        rows.push({
          key: `template:unit:${unitIndex}:${templateRow.category}`,
          category: templateRow.category,
          target: { kind: "unit", unitIndex },
        });
      });
    }
  }
  return rows;
}

//: Owner-requested (2026-08-28): "Built before 1978?" used to be a second,
//: separate question — dropped in favor of deriving it from "Year built"
//: (asked immediately above it in the same step), the same fact typed
//: once rather than twice. `null` (this property's own honest "year
//: unknown" state) derives `null` here too — never a guessed `false`, which
//: would misrepresent an unknown-era property as confirmed-safe for lead
//: paint disclosure purposes.
function deriveBuiltBefore1978(yearBuilt: number | null): boolean | null {
  return yearBuilt === null ? null : yearBuilt < 1978;
}

function formatElapsed(ms: number): string {
  const totalSeconds = Math.max(0, Math.round(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}m ${seconds}s`;
}

export function AddPropertyWizard({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (id: string) => void;
}) {
  const [step, setStep] = useState<WizardStep>("property");

  // -- Step 1: the property's own §4.1 fields --------------------------- //
  const [fullAddress, setFullAddress] = useState("");
  const [nickname, setNickname] = useState("");
  //: Owner-requested (2026-08-27): the nickname used to be silently derived
  //: from the address with no field of its own. This tracks whether the
  //: Owner has actually typed into the real nickname field yet — while
  //: `false`, changing the address keeps the nickname suggestion in sync
  //: with it (the old default behavior, now visible and editable rather
  //: than hidden); the moment the Owner types their own nickname, it stops
  //: following the address.
  const nicknameEditedRef = useRef(false);
  const [municipalityId, setMunicipalityId] = useState("");
  const [propertyType, setPropertyType] = useState("");
  const [yearBuiltInput, setYearBuiltInput] = useState("");

  // -- Step 1: the "create a new town" sub-flow -------------------------- //
  const [showCreateMunicipality, setShowCreateMunicipality] = useState(false);
  const [newMunicipalityName, setNewMunicipalityName] = useState("");
  const [newMunicipalityCounty, setNewMunicipalityCounty] = useState("");
  const [municipalityError, setMunicipalityError] = useState<string | undefined>(undefined);
  const [isCreatingMunicipality, setIsCreatingMunicipality] = useState(false);
  //: Municipalities created inside this wizard instance, merged into the
  //: picker's own options alongside the fetched list — a fresh `POST
  //: /municipalities` does not appear in the already-cached `GET
  //: /municipalities` list without a refetch this wizard has no reason to
  //: force.
  const [createdMunicipalities, setCreatedMunicipalities] = useState<MunicipalityResource[]>([]);
  //: The ids of every town created in this wizard instance whose checklist
  //: has not yet been resolved (either button) — while the *currently
  //: selected* municipality is a member of this set, "Next" is disabled and
  //: its checklist renders (judgement call 4: the checklist is presented
  //: once, right after creation, before the wizard moves on). A `Set`
  //: rather than a single scalar because more than one newly-created town
  //: can be pending at once (create town A, switch away without resolving
  //: it, create town B), and switching the picker back to an unresolved
  //: town — even after switching away to a different, already-resolved one
  //: — must re-show that town's own still-unresolved checklist rather than
  //: silently treating it as done (Story 5.5 Tier A fix round, Fix 2).
  const [pendingChecklistMunicipalityIds, setPendingChecklistMunicipalityIds] = useState<Set<string>>(new Set());

  // -- Step 2: unit rows for every property type but sfh ------------------ //
  const [unitLabels, setUnitLabels] = useState<string[]>([""]);

  // -- Step 3: the baseline equipment proposal (Story 12.2) --------------- //
  //: Every template row's own key that the Owner has unchecked (removed) —
  //: a `Set`, not a filtered copy of the template itself, so the proposal
  //: stays correctly derived from step 2's own current unit labels even
  //: after Back/Forward (spec AC: "survives Back/Forward").
  const [removedTemplateRowKeys, setRemovedTemplateRowKeys] = useState<Set<string>>(new Set());
  //: Hand-added rows beyond the template (spec judgement call 5: always
  //: building-level — see this file's own doc comment above).
  const [extraEquipmentRows, setExtraEquipmentRows] = useState<EquipmentProposalRow[]>([]);
  const [addCategoryValue, setAddCategoryValue] = useState("");
  //: Owner-requested (2026-08-28): keyed by row `key` rather than living on
  //: `EquipmentProposalRow` itself, mirroring `removedTemplateRowKeys`'s own
  //: shape — a template row is regenerated fresh from `templateRowsFor` on
  //: every render (never itself stateful), so anything the Owner types
  //: against one has to live in a side map, the same reason removals do.
  const [rowLocationByKey, setRowLocationByKey] = useState<Record<string, string>>({});

  const equipmentCategoriesQuery = useQuery({
    queryKey: ["reference-list-items", "equipment_category"],
    queryFn: () => apiFetch<Page<ReferenceListItemResource>>("/reference-list-items?list_key=equipment_category"),
    enabled: open,
  });

  // -- Commit ------------------------------------------------------------- //
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [finishError, setFinishError] = useState<string | undefined>(undefined);
  const [result, setResult] = useState<FinishedResult | null>(null);
  //: The clock judgement call 5 asks for — starts the moment the wizard
  //: opens, read again the moment the property is created.
  const openedAtRef = useRef<number | null>(null);

  const municipalitiesQuery = useQuery({
    queryKey: ["municipalities", "list"],
    queryFn: () => apiFetch<Page<MunicipalityResource>>("/municipalities"),
    enabled: open,
  });
  const propertyTypesQuery = useQuery({
    queryKey: ["reference-list-items", "property_type"],
    queryFn: () => apiFetch<Page<ReferenceListItemResource>>("/reference-list-items?list_key=property_type"),
    enabled: open,
  });

  const municipalityOptions = [...(municipalitiesQuery.data?.data ?? [])];
  for (const created of createdMunicipalities) {
    if (!municipalityOptions.some((m) => m.id === created.id)) {
      municipalityOptions.push(created);
    }
  }

  useEffect(() => {
    if (!open) {
      setStep("property");
      setFullAddress("");
      setNickname("");
      nicknameEditedRef.current = false;
      setMunicipalityId("");
      setPropertyType("");
      setYearBuiltInput("");
      setShowCreateMunicipality(false);
      setNewMunicipalityName("");
      setNewMunicipalityCounty("");
      setMunicipalityError(undefined);
      setCreatedMunicipalities([]);
      setPendingChecklistMunicipalityIds(new Set());
      setUnitLabels([""]);
      setRemovedTemplateRowKeys(new Set());
      setExtraEquipmentRows([]);
      setAddCategoryValue("");
      setFinishError(undefined);
      setResult(null);
      openedAtRef.current = null;
    } else if (openedAtRef.current === null) {
      openedAtRef.current = Date.now();
    }
  }, [open]);

  function handleMunicipalityChange(value: string) {
    //: No clearing/re-arming logic needed here beyond selecting the value —
    //: whether the checklist shows is now purely a function of "is this
    //: municipality in the pending set", recomputed on every render. Picking
    //: an already-resolved (or never-new) town shows nothing; picking a
    //: still-unresolved, previously-created town — even after switching away
    //: to something else in between — correctly re-shows its checklist.
    setMunicipalityId(value);
  }

  async function handleCreateMunicipality() {
    if (newMunicipalityName.trim() === "" || newMunicipalityCounty.trim() === "") {
      setMunicipalityError("Enter a town name and county to create one.");
      return;
    }
    setIsCreatingMunicipality(true);
    setMunicipalityError(undefined);
    try {
      const created = await apiFetch<MunicipalityResource>("/municipalities", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: newMunicipalityName.trim(), county: newMunicipalityCounty.trim() }),
      });
      setCreatedMunicipalities((existing) => [...existing, created]);
      setMunicipalityId(created.id);
      setPendingChecklistMunicipalityIds((existing) => new Set(existing).add(created.id));
      setShowCreateMunicipality(false);
      setNewMunicipalityName("");
      setNewMunicipalityCounty("");
    } catch (error) {
      setMunicipalityError(
        error instanceof ApiError ? error.message : "Something went wrong. Please try again in a moment.",
      );
    } finally {
      setIsCreatingMunicipality(false);
    }
  }

  //: Resolves exactly one municipality's own pending checklist (skip or
  //: complete) — removes only that id from the pending set, leaving any
  //: other still-unresolved, newly-created town's own pending entry
  //: untouched (Fix 2b: creating a second town before resolving the first
  //: must not orphan the first one's pending state).
  function resolveChecklist(id: string, updated: MunicipalityResource | undefined) {
    if (updated) {
      setCreatedMunicipalities((existing) => existing.map((m) => (m.id === updated.id ? updated : m)));
    }
    setPendingChecklistMunicipalityIds((existing) => {
      const next = new Set(existing);
      next.delete(id);
      return next;
    });
  }

  const step1Complete =
    fullAddress.trim() !== "" && nickname.trim() !== "" && municipalityId !== "" && propertyType !== "";
  const hasUnresolvedChecklistForSelection = municipalityId !== "" && pendingChecklistMunicipalityIds.has(municipalityId);
  const canAdvanceToUnits = step1Complete && !hasUnresolvedChecklistForSelection;

  const isSingleFamily = propertyType === SINGLE_FAMILY_PROPERTY_TYPE;
  const trimmedUnitLabels = unitLabels.map((label) => label.trim()).filter((label) => label !== "");
  const canFinishUnits = isSingleFamily || trimmedUnitLabels.length > 0;

  //: Step 3's own intended unit-label list — the sfh's one real unit, or
  //: step 2's own current trimmed labels for every other property type.
  //: Deliberately re-derived from step 2's own live state on every render
  //: (never frozen at the moment step 3 first mounts) — a relabel back on
  //: step 2 correctly updates step 3's own per-unit row labels, and no
  //: separate "did the Owner already edit this" flag is needed for that
  //: part, only for the Owner's own removals/additions (below).
  const intendedUnitLabels = isSingleFamily ? [SINGLE_FAMILY_UNIT_LABEL] : trimmedUnitLabels;
  const templateEquipmentRows = templateRowsFor(propertyType, intendedUnitLabels);
  const equipmentRows: EquipmentProposalRow[] = [
    ...templateEquipmentRows.filter((row) => !removedTemplateRowKeys.has(row.key)),
    ...extraEquipmentRows,
  ];
  const equipmentCategoryOptions = equipmentCategoriesQuery.data?.data ?? [];
  const equipmentCategoryLabelByValue: Record<string, string> = {};
  for (const item of equipmentCategoryOptions) {
    equipmentCategoryLabelByValue[item.value] = item.label;
  }
  //: Owner-requested (2026-08-28): every real category is always offered
  //: here now, including one already on the list — the whole point of this
  //: fix is a second refrigerator/AC unit/etc. being a real, addable thing,
  //: not a collision to filter away pre-emptively. `duplicateRowKeys` below
  //: is what makes a resulting duplicate row safe: `create_equipment`'s own
  //: widened natural key (migration `0049`) only refuses two rows sharing
  //: an identical `specific_location` too, so a duplicate is fine the
  //: moment its location is genuinely distinct — a required text field on
  //: any row this identifies, rendered further down.
  const availableExtraCategoryOptions = equipmentCategoryOptions;
  const duplicateKeys = duplicateRowKeys(equipmentRows);
  //: Owner-requested (2026-08-28): a property with exactly one real unit
  //: (sfh, condo, or any other type entered with a single unit label) has
  //: no genuine "building vs. unit" distinction for a hand-added item — it
  //: targets that one real unit directly, both fixing a pre-existing
  //: mistargeting (an sfh's own baseline refrigerator is unit-level, per
  //: `baselineEquipmentTemplates.ts`, but a hand-added one used to land
  //: building-level instead) and being what actually lets a *second*
  //: refrigerator/AC unit collide correctly with the first one's own real
  //: slot rather than an unrelated building-level one. A property with more
  //: than one real unit keeps targeting building-level for a hand-added
  //: extra, as before this fix — a real target-unit picker for that case is
  //: a deliberate, narrower scope left for later, not attempted here.
  const extraEquipmentTarget: EquipmentTarget =
    intendedUnitLabels.length === 1 ? { kind: "unit", unitIndex: 0 } : { kind: "building" };
  //: Owner-requested (2026-08-28): every row `duplicateKeys` flagged needs
  //: a real, non-blank location before Finish is allowed — otherwise two
  //: (or more) of them would still all submit with the identical blank
  //: location, which `create_equipment`'s own new check refuses one at a
  //: time (`EquipmentLocationRequiredError`) — caught only after Finish
  //: was already clicked and silently swallowed by this step's own
  //: sweep-style per-row try/catch (`seedBaselineEquipment`'s own
  //: docstring), leaving the Owner to discover a "missing" fridge later
  //: with no error ever shown. Checked here instead, before Finish is even
  //: reachable.
  const hasMissingEquipmentLocation = [...duplicateKeys].some(
    (key) => (rowLocationByKey[key] ?? "").trim() === "",
  );

  function equipmentRowUnitLabel(row: EquipmentProposalRow): string {
    if (row.target.kind === "building") {
      return "Building-level";
    }
    return intendedUnitLabels[row.target.unitIndex] ?? `Unit ${row.target.unitIndex + 1}`;
  }

  function handleRemoveEquipmentRow(row: EquipmentProposalRow) {
    if (row.key.startsWith("extra:")) {
      setExtraEquipmentRows((existing) => existing.filter((r) => r.key !== row.key));
    } else {
      setRemovedTemplateRowKeys((existing) => new Set(existing).add(row.key));
    }
  }

  function handleAddExtraEquipmentCategory() {
    if (addCategoryValue === "") {
      return;
    }
    setExtraEquipmentRows((existing) => [
      ...existing,
      { key: `extra:${addCategoryValue}:${existing.length}`, category: addCategoryValue, target: extraEquipmentTarget },
    ]);
    setAddCategoryValue("");
  }

  function handleRowLocationChange(row: EquipmentProposalRow, value: string) {
    setRowLocationByKey((existing) => ({ ...existing, [row.key]: value }));
  }

  //: The wizard's own new Story 12.2 seeding step — one `POST /equipment`
  //: per confirmed proposal row, called only after every property/unit
  //: creation call above has already resolved. `sfhUnitId`/`unitOutcomes`
  //: are what let a per-unit row resolve to a *real* unit id: for `sfh`,
  //: the one real unit `POST /properties` itself already created (fetched
  //: fresh here, since that route's own response never echoes it back);
  //: for every other property type, `unitOutcomes`'s own successful
  //: entries only (spec judgement call 4) — a row whose own unit never
  //: actually got created is skipped outright, never sent with a unit_id
  //: that names nothing (VC-12.2-08).
  async function seedBaselineEquipment(propertyId: string, sfhUnitId: string | null, unitOutcomes: UnitOutcome[]) {
    if (equipmentRows.length === 0) {
      return;
    }
    let usefulLifeDefaults: Record<string, number> = {};
    try {
      usefulLifeDefaults = await apiFetch<Record<string, number>>("/equipment/useful-life-defaults");
    } catch {
      // A failed defaults lookup never blocks seeding — every confirmed
      // row still gets created, just without a pre-filled expected_
      // useful_life_years (never a fabricated number either way).
      usefulLifeDefaults = {};
    }

    for (const row of equipmentRows) {
      let unitId: string | null;
      if (row.target.kind === "building") {
        unitId = null;
      } else if (isSingleFamily) {
        if (sfhUnitId === null) {
          continue;
        }
        unitId = sfhUnitId;
      } else {
        const outcome = unitOutcomes[row.target.unitIndex];
        if (outcome === undefined || outcome.status !== "created") {
          // VC-12.2-08 — the unit this row was proposed against never
          // actually, successfully existed; this row is excluded outright,
          // never sent with a dangling unit_id.
          continue;
        }
        unitId = outcome.id;
      }
      //: Owner-requested (2026-08-28): only sent when the Owner actually
      //: typed one — an ordinary single-of-a-kind row still creates with a
      //: blank location, exactly as before this fix (`rowLocationByKey`
      //: only ever gets entries for rows `duplicateKeys` flagged as needing
      //: one, and the Finish button is disabled until every such row has a
      //: real value — see this step's own validation below).
      const location = rowLocationByKey[row.key]?.trim();
      try {
        await apiFetch<EquipmentResource>("/equipment", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            prop_id: propertyId,
            unit_id: unitId,
            category: row.category,
            specific_location: location ? location : null,
            expected_useful_life_years: usefulLifeDefaults[row.category] ?? null,
          }),
        });
      } catch {
        // A natural-key collision — or any other failure — is a silent
        // no-op for this row alone (mirrors `create_equipment`'s own
        // documented sweep contract): never surfaced to the Owner, and
        // never aborting the rest of this sequence (VC-12.2-07).
      }
    }
  }

  async function handleFinish() {
    setIsSubmitting(true);
    setFinishError(undefined);
    try {
      const yearBuilt = yearBuiltInput.trim() === "" ? null : Number(yearBuiltInput);
      const property = await apiFetch<PropertyResource>("/properties", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          nickname: nickname.trim(),
          full_address: fullAddress.trim(),
          municipality_id: municipalityId,
          property_type: propertyType,
          year_built: yearBuilt,
          built_before_1978: deriveBuiltBefore1978(yearBuilt),
        }),
      });

      let sfhUnitId: string | null = null;
      const unitOutcomes: UnitOutcome[] = [];
      if (isSingleFamily) {
        // `POST /properties` never echoes its own single real unit's id
        // back — fetched fresh here, the identical route step 3's own
        // per-unit rows need a real target for (never guessed/invented on
        // the frontend).
        try {
          const units = await apiFetch<Page<UnitResource>>(`/properties/${property.id}/units`);
          sfhUnitId = units.data[0]?.id ?? null;
        } catch {
          sfhUnitId = null;
        }
      } else {
        for (const label of trimmedUnitLabels) {
          try {
            const unit = await apiFetch<UnitResource>(`/properties/${property.id}/units`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ label, intent: "rentable" }),
            });
            unitOutcomes.push({ label: unit.label, id: unit.id, status: "created" });
          } catch (unitError) {
            unitOutcomes.push({
              label,
              status: "failed",
              message: unitError instanceof ApiError ? unitError.message : "Something went wrong.",
            });
          }
        }
      }

      await seedBaselineEquipment(property.id, sfhUnitId, unitOutcomes);

      const elapsedMs = Date.now() - (openedAtRef.current ?? Date.now());
      setResult({ property, unitOutcomes, elapsedMs });
    } catch (error) {
      setFinishError(
        error instanceof ApiError ? error.message : "Something went wrong. Please try again in a moment.",
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleDone() {
    if (result) {
      onOpenChange(false);
      onCreated(result.property.id);
    }
  }

  const title = result
    ? "Property added"
    : step === "property"
      ? "Add a property — address & town"
      : step === "units"
        ? "Add a property — units"
        : "Add a property — equipment";

  return (
    <Dialog open={open} onOpenChange={onOpenChange} title={title}>
      {result ? (
        <div className="mt-4 flex flex-col gap-4">
          <p className="text-body text-ink">
            <strong>{result.property.nickname}</strong> is ready. Added in {formatElapsed(result.elapsedMs)}.
          </p>
          {!isSingleFamily ? (
            <ul className="flex flex-col gap-1">
              {result.unitOutcomes.map((outcome, index) => (
                <li key={`${outcome.label}-${index}`} className="text-body text-ink">
                  {outcome.status === "created" ? (
                    <span>{outcome.label} — created</span>
                  ) : (
                    <span className="text-danger">
                      {outcome.label} — not created ({outcome.message})
                    </span>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-body text-ink-secondary">{SINGLE_FAMILY_UNIT_LABEL} — created</p>
          )}
          {result.unitOutcomes.some((outcome) => outcome.status === "failed") ? (
            <p className="text-body text-ink-secondary">
              Not every unit could be created. The property itself was created successfully — the
              units above that failed were not.
            </p>
          ) : null}
          <div className="flex justify-end gap-2">
            <Link to={`/properties/${result.property.id}`} className="text-body text-accent underline self-center">
              View property
            </Link>
            <Button type="button" onClick={handleDone}>
              Done
            </Button>
          </div>
        </div>
      ) : step === "property" ? (
        <form
          className="mt-4 flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (canAdvanceToUnits) {
              setStep("units");
            }
          }}
        >
          <label className="flex flex-col gap-1">
            <span className="text-label font-medium text-ink-secondary">Address</span>
            <Input
              autoFocus
              value={fullAddress}
              onChange={(event) => {
                const value = event.target.value;
                setFullAddress(value);
                if (!nicknameEditedRef.current) {
                  setNickname(value.trim().slice(0, 200));
                }
              }}
            />
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-label font-medium text-ink-secondary">Nickname</span>
            <Input
              value={nickname}
              onChange={(event) => {
                nicknameEditedRef.current = true;
                setNickname(event.target.value);
              }}
            />
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-label font-medium text-ink-secondary">Municipality</span>
            <Select value={municipalityId} onChange={(event) => handleMunicipalityChange(event.target.value)}>
              <option value="">Choose one…</option>
              {municipalityOptions.map((municipality) => (
                <option key={municipality.id} value={municipality.id}>
                  {municipality.name}, {municipality.county}
                </option>
              ))}
            </Select>
          </label>

          {!showCreateMunicipality ? (
            <Button type="button" variant="ghost" className="self-start" onClick={() => setShowCreateMunicipality(true)}>
              + Create a new town
            </Button>
          ) : (
            <div className="flex flex-col gap-3 rounded-lg border border-border-strong p-4">
              <label className="flex flex-col gap-1">
                <span className="text-label font-medium text-ink-secondary">Town name</span>
                <Input value={newMunicipalityName} onChange={(event) => setNewMunicipalityName(event.target.value)} />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-label font-medium text-ink-secondary">County</span>
                <Input value={newMunicipalityCounty} onChange={(event) => setNewMunicipalityCounty(event.target.value)} />
              </label>
              {municipalityError ? (
                <p role="alert" className="text-meta text-danger">
                  {municipalityError}
                </p>
              ) : null}
              <div className="flex gap-2">
                <Button type="button" onClick={() => void handleCreateMunicipality()} disabled={isCreatingMunicipality}>
                  {isCreatingMunicipality ? "Creating…" : "Create town"}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setShowCreateMunicipality(false);
                    setMunicipalityError(undefined);
                  }}
                  disabled={isCreatingMunicipality}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {hasUnresolvedChecklistForSelection
            ? (() => {
                const pendingMunicipality = municipalityOptions.find((m) => m.id === municipalityId);
                return pendingMunicipality ? (
                  <MunicipalitySetupChecklist
                    municipality={pendingMunicipality}
                    isOwner
                    onComplete={(updated) => resolveChecklist(municipalityId, updated)}
                    onSkip={() => resolveChecklist(municipalityId, undefined)}
                  />
                ) : null;
              })()
            : null}

          <label className="flex flex-col gap-1">
            <span className="text-label font-medium text-ink-secondary">Property type</span>
            <Select value={propertyType} onChange={(event) => setPropertyType(event.target.value)}>
              <option value="">Choose one…</option>
              {(propertyTypesQuery.data?.data ?? []).map((item) => (
                <option key={item.id} value={item.value}>
                  {item.label}
                </option>
              ))}
            </Select>
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-label font-medium text-ink-secondary">Year built</span>
            <Input
              type="number"
              value={yearBuiltInput}
              onChange={(event) => setYearBuiltInput(event.target.value)}
            />
          </label>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canAdvanceToUnits}>
              Next
            </Button>
          </div>
        </form>
      ) : step === "units" ? (
        <div className="mt-4 flex flex-col gap-4">
          {isSingleFamily ? (
            <div className="flex flex-col gap-2">
              <p className="text-body text-ink">
                A single-family property gets one unit automatically, already set up as rentable.
              </p>
              <p className="text-body font-medium text-ink">{SINGLE_FAMILY_UNIT_LABEL}</p>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              <span className="text-label font-medium text-ink-secondary">Units</span>
              {unitLabels.map((label, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Input
                    aria-label={`Unit ${index + 1} label`}
                    value={label}
                    onChange={(event) =>
                      setUnitLabels((existing) => existing.map((l, i) => (i === index ? event.target.value : l)))
                    }
                  />
                  {unitLabels.length > 1 ? (
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => setUnitLabels((existing) => existing.filter((_, i) => i !== index))}
                    >
                      Remove
                    </Button>
                  ) : null}
                </div>
              ))}
              <Button
                type="button"
                variant="ghost"
                className="self-start"
                onClick={() => setUnitLabels((existing) => [...existing, ""])}
              >
                + Add another unit
              </Button>
            </div>
          )}

          <div className="flex justify-between gap-2">
            <Button type="button" variant="ghost" onClick={() => setStep("property")}>
              Back
            </Button>
            <div className="flex gap-2">
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="button" onClick={() => setStep("equipment")} disabled={!canFinishUnits}>
                Next
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="mt-4 flex flex-col gap-4">
          {equipmentRows.length === 0 ? (
            <p className="text-body text-ink-secondary">
              No baseline proposed for this property type — add equipment later from the property or
              equipment page.
            </p>
          ) : (
            <div className="flex flex-col gap-3">
              <span className="text-label font-medium text-ink-secondary">Baseline equipment</span>
              <ul className="flex flex-col gap-2">
                {equipmentRows.map((row) => {
                  const categoryLabel = equipmentCategoryLabelByValue[row.category] ?? row.category;
                  const checkboxId = `equipment-row-${row.key}`;
                  const needsLocation = duplicateKeys.has(row.key);
                  const location = rowLocationByKey[row.key] ?? "";
                  return (
                    <li key={row.key} className="flex flex-col gap-1">
                      <div className="flex items-center gap-2">
                        <CheckboxInput id={checkboxId} checked onChange={() => handleRemoveEquipmentRow(row)} />
                        <label htmlFor={checkboxId} className="text-body text-ink">
                          {categoryLabel} — {equipmentRowUnitLabel(row)}
                        </label>
                      </div>
                      {needsLocation ? (
                        <div className="ml-7 flex flex-col gap-1">
                          <Input
                            aria-label={`Location for this ${categoryLabel}`}
                            placeholder="Location (e.g. Kitchen) — required to tell these apart"
                            value={location}
                            onChange={(event) => handleRowLocationChange(row, event.target.value)}
                            invalid={location.trim() === ""}
                          />
                          {location.trim() === "" ? (
                            <p className="text-meta text-danger">
                              More than one {categoryLabel.toLowerCase()} is proposed here — enter a location for
                              each so they don&apos;t collide.
                            </p>
                          ) : null}
                        </div>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          <div className="flex items-end gap-2">
            <label className="flex flex-1 flex-col gap-1">
              <span className="text-label font-medium text-ink-secondary">Add another category</span>
              <Select value={addCategoryValue} onChange={(event) => setAddCategoryValue(event.target.value)}>
                <option value="">Choose a category…</option>
                {availableExtraCategoryOptions.map((item) => (
                  <option key={item.id} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </Select>
            </label>
            <Button type="button" variant="ghost" onClick={handleAddExtraEquipmentCategory} disabled={addCategoryValue === ""}>
              + Add
            </Button>
          </div>

          {finishError ? (
            <p role="alert" className="text-meta text-danger">
              {finishError}
            </p>
          ) : null}

          <div className="flex justify-between gap-2">
            <Button type="button" variant="ghost" onClick={() => setStep("units")}>
              Back
            </Button>
            <div className="flex gap-2">
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                type="button"
                onClick={() => void handleFinish()}
                disabled={isSubmitting || hasMissingEquipmentLocation}
              >
                {isSubmitting ? "Adding…" : "Finish"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </Dialog>
  );
}
